/** Painel de acessibilidade (US-03, telas L-06 e L-07).
 *
 * A honestidade da UI é requisito aqui, não capricho. Num PDF o conteúdo é
 * imagem rasterizada: família e espaçamento não têm como valer para o texto do
 * livro, e o tamanho só vale via zoom. Mas eles valem para o LEITOR — sumário,
 * painel de destaques, menus —, então desabilitá-los seria mentira, e é a
 * mentira mais cara: o aluno disléxico quer OpenDyslexic no sumário e nos
 * próprios destaques, e desabilitar tiraria dele o que o controle faz de
 * verdade. Ficam habilitados, com o escopo escrito ao lado.
 *
 * É `<dialog>`-like por marcação (`role="dialog"` + `aria-modal`), com foco
 * indo para o título ao abrir, `Escape` fechando e o foco voltando ao botão que
 * o abriu — sem isso, quem navega por teclado fecha o painel e cai no topo do
 * documento.
 */
import { useEffect, useRef } from 'react'

import { useAccessibilityStore } from '@/features/reader/stores/accessibilityStore'
import { useReaderStore } from '@/features/reader/stores/readerStore'
import type { BookFormat, ReaderPreferences } from '@/types/api'

const TEMAS: { valor: ReaderPreferences['theme']; rotulo: string }[] = [
  { valor: 'light', rotulo: 'Claro' },
  { valor: 'dark', rotulo: 'Escuro' },
  { valor: 'sepia', rotulo: 'Sépia' },
]

const ESPACAMENTOS: { valor: ReaderPreferences['line_spacing']; rotulo: string }[] = [
  { valor: 'compact', rotulo: 'Compacto' },
  { valor: 'normal', rotulo: 'Normal' },
  { valor: 'wide', rotulo: 'Amplo' },
]

const FAMILIAS: { valor: ReaderPreferences['font_family']; rotulo: string }[] = [
  { valor: 'publisher', rotulo: 'Da editora' },
  { valor: 'georgia', rotulo: 'Georgia' },
  { valor: 'opendyslexic', rotulo: 'OpenDyslexic' },
]

const LARGURAS: { valor: ReaderPreferences['column_width']; rotulo: string }[] = [
  { valor: 'narrow', rotulo: 'Estreita' },
  { valor: 'normal', rotulo: 'Normal' },
  { valor: 'wide', rotulo: 'Larga' },
]

interface AccessibilityPanelProps {
  /** Decide o que se aplica ao conteúdo; o chrome do leitor sempre obedece. */
  formato: BookFormat
}

export function AccessibilityPanel({ formato }: AccessibilityPanelProps) {
  const aberto = useReaderStore((s) => s.acessibilidadeAberta)
  const alternar = useReaderStore((s) => s.alternarAcessibilidade)
  const preferencias = useAccessibilityStore((s) => s.preferencias)
  const definir = useAccessibilityStore((s) => s.definir)
  const restaurarPadroes = useAccessibilityStore((s) => s.restaurarPadroes)

  const tituloRef = useRef<HTMLHeadingElement>(null)
  const focoAnterior = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!aberto) return
    focoAnterior.current = document.activeElement as HTMLElement | null
    tituloRef.current?.focus()

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        alternar()
        focoAnterior.current?.focus()
      }
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [aberto, alternar])

  if (!aberto) return null

  const ehPdf = formato === 'pdf'
  // Dito antes, é escopo; descoberto depois, é defeito.
  const notaPdf = 'Vale para o sumário e os painéis; o texto dentro do PDF é uma imagem da página.'

  return (
    <div
      className="a11y-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="a11y-titulo"
      data-testid="a11y-panel"
    >
      <div className="a11y-panel__cabecalho">
        <h2 id="a11y-titulo" ref={tituloRef} tabIndex={-1}>
          Configurações de leitura
        </h2>
        <button
          type="button"
          onClick={() => {
            alternar()
            focoAnterior.current?.focus()
          }}
          aria-label="Fechar configurações de leitura"
        >
          ✕
        </button>
      </div>

      <div className="a11y-panel__corpo">
        <fieldset className="a11y-grupo">
          <legend>Tamanho do texto</legend>
          <div className="a11y-slider">
            <input
              type="range"
              min={80}
              max={160}
              step={10}
              value={preferencias.font_scale}
              onChange={(e) => definir('font_scale', Number(e.target.value))}
              aria-label="Tamanho do texto"
              aria-valuetext={`${preferencias.font_scale} por cento`}
            />
            <output className="a11y-slider__valor">{preferencias.font_scale}%</output>
          </div>
          {ehPdf && (
            // Explicar em vez de surpreender: o aluno arrasta esperando texto
            // maior e a PÁGINA inteira cresce.
            <p className="a11y-nota">
              Em PDF, aumentar o texto aplica zoom na página inteira.
            </p>
          )}
        </fieldset>

        <fieldset className="a11y-grupo">
          <legend>Tema</legend>
          <div className="a11y-opcoes">
            {TEMAS.map((tema) => (
              <button
                key={tema.valor}
                type="button"
                className="a11y-opcao"
                aria-pressed={preferencias.theme === tema.valor}
                onClick={() => definir('theme', tema.valor)}
              >
                {tema.rotulo}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="a11y-grupo">
          <legend>Contraste e cores</legend>
          <label className="a11y-check">
            <input
              type="checkbox"
              checked={preferencias.high_contrast}
              onChange={(e) => definir('high_contrast', e.target.checked)}
            />
            Alto contraste
          </label>
          {ehPdf && (
            <>
              <label className="a11y-check">
                <input
                  type="checkbox"
                  checked={preferencias.invert_pdf}
                  onChange={(e) => definir('invert_pdf', e.target.checked)}
                />
                Inverter as cores da página
              </label>
              {/* A ressalva é o motivo de a caixa existir separada do tema: num
                  livro didático, foto e mapa invertidos viram negativo. */}
              <p className="a11y-nota">
                Fotos e mapas ficam em negativo quando a página é invertida.
              </p>
            </>
          )}
        </fieldset>

        <fieldset className="a11y-grupo">
          <legend>Fonte do texto</legend>
          <div className="a11y-opcoes">
            {FAMILIAS.map((familia) => (
              <button
                key={familia.valor}
                type="button"
                className="a11y-opcao"
                aria-pressed={preferencias.font_family === familia.valor}
                onClick={() => definir('font_family', familia.valor)}
              >
                {familia.rotulo}
              </button>
            ))}
          </div>
          {ehPdf && <p className="a11y-nota">{notaPdf}</p>}
        </fieldset>

        <fieldset className="a11y-grupo">
          <legend>Espaçamento entre linhas</legend>
          <div className="a11y-opcoes">
            {ESPACAMENTOS.map((espaco) => (
              <button
                key={espaco.valor}
                type="button"
                className="a11y-opcao"
                aria-pressed={preferencias.line_spacing === espaco.valor}
                onClick={() => definir('line_spacing', espaco.valor)}
              >
                {espaco.rotulo}
              </button>
            ))}
          </div>
          {ehPdf && <p className="a11y-nota">{notaPdf}</p>}
        </fieldset>

        <fieldset className="a11y-grupo">
          <legend>Largura da coluna</legend>
          <div className="a11y-opcoes">
            {LARGURAS.map((largura) => (
              <button
                key={largura.valor}
                type="button"
                className="a11y-opcao"
                aria-pressed={preferencias.column_width === largura.valor}
                onClick={() => definir('column_width', largura.valor)}
              >
                {largura.rotulo}
              </button>
            ))}
          </div>
          {ehPdf && (
            <p className="a11y-nota">
              Também limita a largura com que a página do PDF é desenhada.
            </p>
          )}
        </fieldset>

        <fieldset className="a11y-grupo">
          <legend>Leitura em voz alta</legend>
          <label className="a11y-check">
            <input
              type="checkbox"
              checked={preferencias.tts_enabled}
              onChange={(e) => definir('tts_enabled', e.target.checked)}
            />
            Ler a página automaticamente ao abrir
          </label>
          {/* O BOTÃO de ouvir está sempre na barra de páginas: esconder recurso
              de acessibilidade atrás de configuração obriga justamente quem
              mais precisa dele a achar a configuração primeiro. Este
              interruptor é para a leitura CONTÍNUA. */}
          <p className="a11y-nota">
            O botão “Ouvir” continua disponível na barra de páginas.
          </p>
          <div className="a11y-slider">
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.25}
              value={preferencias.tts_rate}
              onChange={(e) => definir('tts_rate', Number(e.target.value))}
              aria-label="Velocidade da voz"
              aria-valuetext={`${preferencias.tts_rate} vezes`}
            />
            <output className="a11y-slider__valor">{preferencias.tts_rate}×</output>
          </div>
        </fieldset>

        <fieldset className="a11y-grupo">
          <legend>Movimento</legend>
          <label className="a11y-check">
            <input
              type="checkbox"
              checked={preferencias.reduce_motion === 'always'}
              onChange={(e) => definir('reduce_motion', e.target.checked ? 'always' : 'auto')}
            />
            Reduzir animações
          </label>
          <p className="a11y-nota">
            Sem marcar, a preferência do seu sistema continua valendo.
          </p>
        </fieldset>

        <button type="button" className="a11y-restaurar" onClick={restaurarPadroes}>
          Restaurar padrões
        </button>
      </div>
    </div>
  )
}
